import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import NotificationBell from './NotificationBell';
import Chatbot from './Chatbot';
import SignInModal from './SignInModal';
import { Button } from './ui/button';
import { Scan, Menu, X, ShoppingCart, Calendar, Sparkles, Wrench, ChevronDown, QrCode, Mail, Video, Link2, CreditCard, ScanLine, FileText, LogIn } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext';
import { useCart } from '../contexts/CartContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from './ui/dropdown-menu';

const Layout = ({ children, userCard, loading }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const { user } = useAuth();
  const { isCollapsed } = useSidebar();
  const { cart, itemCount } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we're on a bio page
  const isBioPage = location.pathname.startsWith('/bio/');

  const handleScanClick = () => {
    navigate('/contacts?scan=true');
  };

  return (
    <div className={`min-h-screen flex flex-col ${isBioPage ? 'bg-transparent' : 'bg-gray-50'}`}>
      {/* Header - Dark Gray Background - Always Fixed */}
      <header className="header-dark-bg border-b-2 border-gray-700 fixed top-0 left-0 right-0 z-[60] shadow-lg">
        <div className="max-w-full mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16 gap-2">
            {/* Left side: Mobile Menu + Logo */}
            <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
              {/* Mobile Menu Toggle - Always visible on mobile */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-lg bg-gray-800 hover:bg-gray-700 text-white transition-colors shadow-md border border-gray-700 flex-shrink-0"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? (
                  <X className="w-5 h-5 sm:w-6 sm:h-6" />
                ) : (
                  <Menu className="w-5 h-5 sm:w-6 sm:h-6" />
                )}
              </button>
              
              <Link to="/" className="flex items-center space-x-1.5 sm:space-x-2 min-w-0">
                <img 
                  src="/logo.png" 
                  alt="Piper Card Logo" 
                  className="h-7 sm:h-8 md:h-10 w-auto object-contain flex-shrink-0"
                />
                <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                  <span className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-amber-500 to-yellow-400 bg-clip-text text-transparent truncate">
                    Piper Card
                  </span>
                </div>
              </Link>

              {/* Tools Dropdown Menu - Only show when not logged in */}
              {!user && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button 
                      className="hidden sm:flex items-center gap-1.5 px-3 py-2 text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition-colors text-sm font-medium"
                      data-testid="tools-dropdown-trigger"
                    >
                      <Wrench className="w-4 h-4" />
                      <span>Tools</span>
                      <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent 
                    align="start" 
                    className="w-64 bg-gray-900 border-gray-700 text-gray-100"
                    data-testid="tools-dropdown-content"
                  >
                    <DropdownMenuLabel className="text-amber-400 text-xs uppercase tracking-wider">
                      Free Marketing Tools
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-gray-700" />
                    
                    <DropdownMenuItem 
                      className="cursor-pointer hover:bg-gray-800 focus:bg-gray-800 focus:text-white"
                      onClick={() => navigate('/email-signature-generator')}
                      data-testid="tools-menu-email-signature"
                    >
                      <Mail className="w-4 h-4 mr-2 text-amber-400" />
                      <div>
                        <div className="font-medium">Email Signature Generator</div>
                        <div className="text-xs text-gray-400">Create professional email signatures</div>
                      </div>
                    </DropdownMenuItem>

                    <DropdownMenuItem 
                      className="cursor-pointer hover:bg-gray-800 focus:bg-gray-800 focus:text-white"
                      onClick={() => navigate('/meeting-themes-generator')}
                      data-testid="tools-menu-meeting-themes"
                    >
                      <Video className="w-4 h-4 mr-2 text-blue-400" />
                      <div>
                        <div className="font-medium">Meeting Background Generator</div>
                        <div className="text-xs text-gray-400">Custom Zoom/Teams backgrounds</div>
                      </div>
                    </DropdownMenuItem>

                    <DropdownMenuSeparator className="bg-gray-700" />

                    <DropdownMenuItem 
                      className="cursor-pointer hover:bg-gray-800 focus:bg-gray-800 focus:text-white"
                      onClick={() => navigate('/card-builder')}
                      data-testid="tools-menu-digital-card"
                    >
                      <CreditCard className="w-4 h-4 mr-2 text-amber-400" />
                      <div>
                        <div className="font-medium">Digital Business Card Maker</div>
                        <div className="text-xs text-gray-400">Create your digital card</div>
                      </div>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
            
            {/* Right side: Cart + Buttons */}
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Cart Button - Always visible when items in cart */}
              {itemCount > 0 && (
                <button
                  onClick={() => navigate('/cart')}
                  className="relative flex items-center gap-1.5 px-3 py-1.5 sm:py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-all shadow-md font-semibold text-sm"
                  data-testid="header-cart-btn"
                >
                  <ShoppingCart className="w-4 h-4" />
                  <span className="hidden sm:inline">Cart</span>
                  <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow-lg animate-pulse">
                    {itemCount}
                  </span>
                </button>
              )}
              
              {/* Buttons for logged-in users */}
              {user && (
                <>
                  <Button
                    onClick={handleScanClick}
                    className="bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white font-bold px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 shadow-md h-8 sm:h-9 md:h-10 flex-shrink-0 text-xs sm:text-sm md:text-base"
                  >
                    <Scan className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 mr-1 sm:mr-1.5" />
                    <span>Scan</span>
                  </Button>
                  <NotificationBell />
                </>
              )}

              {/* Buttons for non-logged-in users */}
              {!user && (
                <>
                  {/* Book Demo Button - Hidden on smallest screens */}
                  <Button
                    onClick={() => navigate('/book-demo')}
                    variant="outline"
                    className="hidden sm:flex items-center gap-1.5 border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-white px-3 py-1.5 h-9 text-sm"
                    data-testid="header-book-demo-btn"
                  >
                    <Calendar className="w-4 h-4" />
                    <span className="hidden md:inline">Book Demo</span>
                  </Button>
                  
                  {/* Create Card Button */}
                  <Button
                    onClick={() => navigate('/card-builder')}
                    className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold px-3 sm:px-4 py-1.5 h-9 text-sm shadow-md"
                    data-testid="header-create-card-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-1.5 hidden sm:inline" />
                    <span>Create Card</span>
                  </Button>
                  
                  {/* Sign In Button */}
                  <Button
                    onClick={() => setShowSignInModal(true)}
                    variant="outline"
                    className="flex items-center gap-1.5 border-amber-500 text-amber-400 hover:bg-amber-500 hover:text-white px-3 py-1.5 h-9 text-sm font-medium"
                    data-testid="header-sign-in-btn"
                  >
                    <LogIn className="w-4 h-4" />
                    <span className="hidden sm:inline">Sign In</span>
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <Sidebar 
        userCard={userCard} 
        loading={loading} 
        isMobileOpen={isMobileMenuOpen}
        setIsMobileOpen={setIsMobileMenuOpen}
      />

      {/* Main Content with proper spacing for sidebar and fixed header */}
      <main className="flex-grow transition-all duration-300 lg:ml-20 pt-14 sm:pt-16">
        {children}
      </main>

      {/* AI Chatbot - Available on all pages, before and after login */}
      <Chatbot />

      {/* Footer - Show full footer for guests, only copyright for logged in users */}
      {user ? (
        // Footer for logged-in users with Pricing link
        <footer className={`bg-gray-900 text-gray-300 py-4 border-t border-gray-800 transition-all duration-300 lg:ml-20`}>
          <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center gap-4 text-sm">
              <Link to="/pricing" className="text-gray-400 hover:text-amber-400 transition-colors">Pricing</Link>
              <span className="text-amber-400 font-medium">NFC Cards @ ₹999</span>
              <span className="text-gray-500">© {new Date().getFullYear()} Piper Card</span>
            </div>
          </div>
        </footer>
      ) : (
        // Compact footer for guests on bio pages
        <footer className={`bg-gray-900 text-gray-300 py-6 border-t border-gray-800 transition-all duration-300 lg:ml-20`}>
          <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              {/* Brand */}
              <Link to="/" className="flex items-center gap-2">
                <img src="/logo.png" alt="Piper Card" className="h-6 w-auto" />
                <span className="font-semibold text-white text-sm">Piper Card</span>
              </Link>
              
              {/* Quick Links + Pricing */}
              <div className="flex items-center gap-4 text-xs text-gray-400">
                <Link to="/shop" className="hover:text-amber-400 transition-colors">NFC Cards</Link>
                <Link to="/pricing" className="hover:text-amber-400 transition-colors">Pricing</Link>
                <span className="text-amber-400 font-medium">NFC Cards @ ₹999</span>
                <Link to="/terms-and-conditions" className="hover:text-amber-400 transition-colors">Terms</Link>
              </div>
              
              {/* Social + Copyright */}
              <div className="flex items-center gap-3">
                <a href="https://www.instagram.com/pipercard/" target="_blank" rel="noopener noreferrer" className="w-7 h-7 bg-gray-800 hover:bg-pink-600 rounded-full flex items-center justify-center transition-colors" title="Instagram">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </a>
                <span className="text-xs text-gray-500">© {new Date().getFullYear()} Piper Card</span>
              </div>
            </div>
          </div>
        </footer>
      )}
      
      {/* Sign In Modal */}
      <SignInModal 
        open={showSignInModal} 
        onOpenChange={setShowSignInModal}
        redirectPath="/profile"
      />
    </div>
  );
};

export default Layout;
